import React from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { IoWarningOutline, IoCutSharp } from "react-icons/io5";
import {
  HiClock,
  HiX,
  HiSparkles,
  HiAdjustments,
  HiExclamation,
} from "react-icons/hi";
import Loader from "components/common/Loader/Loader";
import useCareLabels from "hooks/useCareLabels";
import { getAdvanceDurationLabel } from "./advanceModeValidation";

/** Shared class for native time inputs inside advance mode. */
export const ADVANCE_TIME_INPUT_CLASS = "advance-mode-time-input";

const THEME_ICON = {
  violet: "bg-violet-100 text-violet-600 ring-violet-500/20",
  amber: "bg-amber-100 text-amber-600 ring-amber-500/20",
  emerald: "bg-emerald-100 text-emerald-600 ring-emerald-500/20",
  sky: "bg-sky-100 text-sky-600 ring-sky-500/20",
};

const CARD_ACCENT = {
  default: "border-l-slate-300",
  participant: "border-l-emerald-400",
  open: "border-l-sky-400",
};

export const advanceSectionClass =
  "advance-mode-section min-w-0 max-w-full overflow-visible rounded-xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)] ring-1 ring-slate-900/[0.03]";

export const AdvanceModeSection = ({ children, className = "", ...rest }) => (
  <section className={`${advanceSectionClass} ${className}`} {...rest}>
    {children}
  </section>
);

export const AdvanceModeSectionHeader = ({
  icon: Icon,
  title,
  subtitle,
  action,
  theme = "violet",
  bordered = true,
  compact = false,
}) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between ${
      compact
        ? "gap-1.5 px-3 py-1.5 sm:gap-2 sm:px-3 sm:py-1.5"
        : "gap-2 px-3 py-3 sm:gap-3 sm:px-4 sm:py-3.5"
    } ${
      bordered
        ? "border-b border-slate-100/90 bg-gradient-to-r from-slate-50/90 via-white to-white"
        : ""
    }`}
  >
    <div
      className={`flex min-w-0 flex-1 items-center ${
        compact ? "gap-2" : "gap-2.5 sm:gap-3"
      }`}
    >
      {Icon && (
        <div
          className={`flex shrink-0 items-center justify-center rounded-xl ring-1 ring-inset shadow-sm ${
            compact ? "h-7 w-7" : "h-9 w-9"
          } ${THEME_ICON[theme] || THEME_ICON.violet}`}
        >
          <Icon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </div>
      )}
      <div className='min-w-0'>
        <h3
          className={`font-semibold tracking-tight text-slate-900 ${
            compact ? "text-xs" : "text-sm"
          }`}
        >
          {title}
        </h3>
        {subtitle && (
          <p className='mt-0.5 text-[11px] leading-snug text-slate-500'>{subtitle}</p>
        )}
      </div>
    </div>
    {action && (
      <div className='flex w-full shrink-0 items-center sm:w-auto sm:justify-end'>
        {action}
      </div>
    )}
  </div>
);

export const AdvanceModeSectionBody = ({
  children,
  className = "",
  noPadding,
  compact = false,
}) => (
  <div
    className={
      noPadding
        ? className
        : `${
            compact
              ? "px-3 py-2 sm:px-3 sm:py-2"
              : "px-3 py-3 sm:px-4 sm:py-3.5"
          } ${className}`
    }
  >
    {children}
  </div>
);

export const AdvanceModeCountBadge = ({ count, label, tone = "slate" }) => {
  if (count == null) return null;
  const tones = {
    slate: "bg-slate-100 text-slate-700 ring-slate-200/80",
    emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200/80",
    sky: "bg-sky-50 text-sky-800 ring-sky-200/80",
    violet: "bg-violet-50 text-violet-800 ring-violet-200/80",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ring-1 ring-inset ${
        tones[tone] || tones.slate
      }`}
    >
      <span className='tabular-nums'>{count}</span>
      {label && <span className='font-medium opacity-80'>{label}</span>}
    </span>
  );
};

export const AdvanceModeStaBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-indigo-200/80 ${className}`}
    title='STA defaults apply from this participant'
  >
    <HiSparkles className='h-3 w-3' aria-hidden />
    STA template
  </span>
);

export const AdvanceModeInfoCallout = ({
  children,
  tone = "amber",
  icon: Icon,
  className = "",
}) => {
  const tones = {
    amber: "border-amber-200/90 bg-amber-50/90 text-amber-950",
    emerald: "border-emerald-200/90 bg-emerald-50/90 text-emerald-950",
    sky: "border-sky-200/90 bg-sky-50/90 text-sky-950",
    slate: "border-slate-200/90 bg-slate-50/90 text-slate-800",
  };
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[11px] leading-snug ${tones[tone] || tones.amber} ${className}`}
      role='status'
    >
      {Icon && <Icon className='mt-0.5 h-4 w-4 shrink-0 opacity-80' aria-hidden />}
      <div className='min-w-0'>{children}</div>
    </div>
  );
};

const formatWorkerNameList = (names = []) => {
  const list = names.filter(Boolean);
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

/** Informational banner when workers outside Common Workers are selected. */
export const AdvanceModeNonCommonWorkerWarning = ({ workers = [] }) => {
  if (!workers?.length) return null;

  const names = workers.map((worker) => worker?.name).filter(Boolean);
  if (!names.length) return null;

  const nameText = formatWorkerNameList(names);
  const workerLabel = names.length === 1 ? "worker" : "workers";

  return (
    <div
      className='mt-2.5 overflow-hidden rounded-xl border border-amber-200/90 bg-gradient-to-r from-amber-50 via-orange-50/40 to-amber-50/80 shadow-sm ring-1 ring-amber-100/80'
      role='status'
    >
      <div className='flex items-start gap-3 px-3 py-2.5 sm:px-3.5 sm:py-3'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200/80'>
          <HiExclamation className='h-4 w-4' aria-hidden />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-xs font-semibold text-amber-950'>
            Worker not linked to all selected participants
          </p>
          <p className='mt-1 text-[11px] leading-relaxed text-amber-900/90'>
            <span className='font-semibold capitalize'>{nameText}</span>
            {names.length === 1 ? " is" : " are"} not in the{" "}
            <span className='font-semibold'>Common Workers</span> list for your current
            participant selection. This {workerLabel} may not be assigned to every
            selected participant — please confirm before saving.
          </p>
        </div>
      </div>
    </div>
  );
};

/** Shared copy for banner + update confirmation when original workers are removed. */
export const getRemovedWorkerTimesheetWarningCopy = (workers = []) => {
  const names = workers
    .map((worker) => worker?.name || worker?.label || null)
    .filter(Boolean);
  if (!names.length) return null;

  const nameText = formatWorkerNameList(names);
  const isSingle = names.length === 1;
  const title = `Removing worker${isSingle ? "" : "s"} will also remove ${
    isSingle ? "their timesheet" : "their timesheets"
  }`;
  const content =
    `${nameText}${isSingle ? " was" : " were"} removed from this shift. ` +
    `If you continue and save, ${
      isSingle
        ? "this worker's timesheet and related participant notes will also be deleted."
        : "these workers' timesheets and related participant notes will also be deleted."
    }`;

  return { title, content, nameText, isSingle };
};

/**
 * Danger banner when an originally assigned worker is removed from the dropdown.
 * Warns that saving will also remove that worker's timesheet(s).
 */
export const AdvanceModeRemovedWorkerTimesheetWarning = ({
  workers = [],
  className = "",
}) => {
  const copy = getRemovedWorkerTimesheetWarningCopy(workers);
  if (!copy) return null;

  return (
    <div
      className={`advance-timesheet-remove-warn mt-2.5 rounded-xl ${className}`}
      role='alert'
    >
      <div className='flex items-start gap-3 px-3 py-2.5 sm:px-3.5 sm:py-3'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600'>
          <FaExclamationCircle className='h-4 w-4' aria-hidden />
        </div>
        <div className='min-w-0 flex-1 text-left'>
          <p className='text-xs font-semibold text-rose-950'>{copy.title}</p>
          <p className='mt-1 text-[11px] leading-relaxed text-rose-900/85'>
            {copy.content}
          </p>
        </div>
      </div>
    </div>
  );
};

const DURATION_TONES = {
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
  emerald: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  sky: "bg-sky-100 text-sky-700 ring-sky-200",
  violet: "bg-violet-100 text-violet-700 ring-violet-200",
};

export const AdvanceModeDurationBadge = ({
  start,
  end,
  tone = "slate",
  className = "",
}) => {
  const label = getAdvanceDurationLabel(start, end);
  if (!label) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
        DURATION_TONES[tone] || DURATION_TONES.slate
      } ${className}`}
      title='Duration'
    >
      <HiClock className='h-3 w-3' aria-hidden />
      {label}
    </span>
  );
};

export const AdvanceModeTimeRange = ({ startField, endField, arrow = "→" }) => (
  <div className='advance-mode-time-range'>
    <div className='advance-mode-time-range__cell min-w-0'>{startField}</div>
    <span className='advance-mode-time-range__arrow' aria-hidden>
      {arrow}
    </span>
    <div className='advance-mode-time-range__cell min-w-0'>{endField}</div>
  </div>
);

/** Reserved error slot — keeps start/end inputs aligned when only one side errors. */
export const AdvanceModeFieldError = ({ message }) => (
  <p
    className={`advance-mode-field-error${
      message ? " advance-mode-field-error--visible" : ""
    }`}
    role={message ? "alert" : undefined}
  >
    {message || "\u00a0"}
  </p>
);

/** Shared validation message spanning the full time range (e.g. same-time / zero duration). */
export const AdvanceModeTimeRangeSharedError = ({ message }) => {
  if (!message) return null;
  return (
    <p className='advance-mode-time-range-shared-error' role='alert'>
      {message}
    </p>
  );
};

export const AdvanceModeDivider = ({ label }) => (
  <div className='relative my-3.5' aria-hidden={!label}>
    <div className='absolute inset-0 flex items-center'>
      <div className='w-full border-t border-slate-200/90' />
    </div>
    {label && (
      <div className='relative flex justify-start'>
        <span className='bg-white pr-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400'>
          {label}
        </span>
      </div>
    )}
  </div>
);

export const AdvanceModeFieldLabel = ({ children, required }) => (
  <label className='mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500'>
    {children}
    {required && <span className='normal-case text-rose-500'> *</span>}
  </label>
);

export const AdvanceModeEmptyState = ({ icon: Icon, title, description, action }) => (
  <div className='flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200/90 bg-gradient-to-b from-slate-50/80 to-white px-5 py-8 text-center'>
    {Icon && (
      <div className='mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100/90 ring-1 ring-slate-200/60'>
        <Icon className='h-5 w-5 text-slate-400' />
      </div>
    )}
    {title && <p className='text-xs font-semibold text-slate-700'>{title}</p>}
    {description && (
      <p className='mt-1.5 max-w-[260px] text-[11px] leading-relaxed text-slate-500'>
        {description}
      </p>
    )}
    {action && <div className='mt-4 flex w-full justify-center'>{action}</div>}
  </div>
);

const CARD_VARIANTS = {
  default:
    "border-slate-200/80 bg-gradient-to-br from-white via-white to-slate-50/50 hover:border-slate-300/80 hover:shadow-md",
  participant:
    "border-emerald-200/60 bg-gradient-to-br from-white via-white to-emerald-50/25 hover:border-emerald-300/70 hover:shadow-md",
  open: "border-dashed border-sky-300/80 bg-gradient-to-br from-sky-50/35 to-white hover:border-sky-400/80 hover:shadow-md",
};

export const AdvanceModeItemCard = ({ children, variant = "default", className = "" }) => (
  <div
    className={`min-w-0 max-w-full rounded-xl border border-l-[3px] p-3 shadow-sm transition-all duration-200 sm:p-3.5 ${CARD_VARIANTS[variant]} ${CARD_ACCENT[variant] || CARD_ACCENT.default} ${className}`}
  >
    {children}
  </div>
);

const AVATAR_TONES = {
  emerald: "bg-emerald-100 text-emerald-800 ring-emerald-200/70",
  sky: "bg-sky-100 text-sky-800 ring-sky-200/70",
  slate: "bg-slate-100 text-slate-700 ring-slate-200/70",
};

  export const AdvanceModeEntityAvatar = ({ name, tone = "slate", label }) => {
  const initial =
    label != null
      ? String(label)
      : String(name || "?")
          .trim()
          .charAt(0)
          .toUpperCase();
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1 ring-inset ${
        AVATAR_TONES[tone] || AVATAR_TONES.slate
      }`}
      aria-hidden
    >
      {initial}
    </span>
  );
};

export const AdvanceModeCardHeader = ({
  name,
  subtitle,
  badge,
  extra,
  tone = "slate",
  avatarLabel,
  statusTag = null,
  className = "",
}) => (
  <div className={`mb-3 flex min-w-0 items-start gap-2.5 pr-1 ${className}`}>
    <span className={`relative inline-flex shrink-0 ${statusTag ? "pb-1" : ""}`}>
      <AdvanceModeEntityAvatar name={name} tone={tone} label={avatarLabel} />
      {statusTag}
    </span>
    <div className='min-w-0 flex-1'>
      <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
        <p className='min-w-0 truncate text-sm font-semibold capitalize text-slate-900'>
          {name}
        </p>
        {badge}
      </div>
      {subtitle && (
        <p className='mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500'>
          {subtitle}
        </p>
      )}
    </div>
    {extra && <div className='flex shrink-0 flex-wrap items-center gap-1.5'>{extra}</div>}
  </div>
);

export const AdvanceModeIconButton = ({
  onClick,
  title,
  disabled,
  tone = "danger",
}) => {
  const tones = {
    danger:
      "border border-rose-200/70 bg-white text-rose-400 shadow-sm ring-1 ring-rose-100/50 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 hover:shadow-md active:scale-95 focus-visible:ring-rose-400/35",
    participant:
      "border border-emerald-200/60 bg-white text-emerald-600/70 shadow-sm ring-1 ring-emerald-100/40 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-500 hover:ring-rose-100/60 hover:shadow-md active:scale-95 focus-visible:ring-rose-300/35",
    muted:
      "border border-slate-200/80 bg-white text-slate-400 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600 focus-visible:ring-slate-300/50 active:scale-95",
  };
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`group flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone] || tones.danger}`}
    >
      <svg
        className='h-3 w-3 transition-transform duration-200 group-hover:scale-110'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={2.25}
        aria-hidden
      >
        <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
      </svg>
    </button>
  );
};

export const AdvanceModeParticipantRemoveButton = ({
  onClick,
  disabled,
  title = "Remove breakdown",
}) => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className='group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-white text-slate-500 shadow-sm transition-all duration-200 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 hover:shadow-md active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40 disabled:cursor-not-allowed disabled:opacity-40'
  >
    <HiX
      className='h-3.5 w-3.5 stroke-[2.5px] transition-transform duration-200 group-hover:rotate-90'
      aria-hidden
    />
  </button>
);

export const AdvanceModeActionButton = ({
  children,
  onClick,
  disabled,
  variant = "primary",
  className = "",
}) => {
  const variants = {
    primary:
      "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 focus-visible:ring-indigo-500/40",
    secondary:
      "border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:ring-slate-300/50",
    ghost:
      "border border-dashed border-sky-300/80 bg-sky-50/50 text-sky-700 hover:border-sky-400 hover:bg-sky-50 focus-visible:ring-sky-400/40",
  };
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

/** Split billing/time segment — compact footer action on participant cards. */
export const AdvanceModeSplitSegmentButton = ({
  onClick,
  disabled,
  className = "",
  title = "Split breakdown",
}) => (
  <div
    className={`advance-mode-split-segment mt-3 border-t border-slate-100/90 pt-3 ${className}`}
  >
    <p className='advance-mode-split-segment__hint'>
      <IoCutSharp className='advance-mode-split-segment__hint-icon' aria-hidden />
      <span>Bill different times or line items in this window</span>
    </p>
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className='advance-mode-split-segment__btn'
    >
      <IoCutSharp className='h-3.5 w-3.5' aria-hidden />
      {title}
    </button>
  </div>
);

/** DOM ids for advance mode section anchor navigation. */
export const ADVANCE_MODE_SECTION_IDS = {
  schedule: "advance-mode-section-schedule",
  participants: "advance-mode-section-participants",
  workers: "advance-mode-section-workers",
  tasksNotes: "advance-mode-section-tasks-notes",
};

const scrollToAdvanceSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (!element) return;

  const scrollContainer = element.closest(".custom-scroll");
  if (scrollContainer && typeof scrollContainer.scrollTo === "function") {
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const top =
      elementRect.top - containerRect.top + scrollContainer.scrollTop - 20;

    scrollContainer.scrollTo({
      top: Math.max(0, top),
      behavior: "smooth",
    });
    return;
  }

  element.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ADVANCE_HEADER_STEPS = [
  {
    step: 1,
    label: "Schedule",
    theme: "violet",
    sectionId: ADVANCE_MODE_SECTION_IDS.schedule,
  },
  {
    step: 2,
    labelKey: "participants",
    theme: "emerald",
    sectionId: ADVANCE_MODE_SECTION_IDS.participants,
  },
  {
    step: 3,
    label: "Workers",
    theme: "sky",
    sectionId: ADVANCE_MODE_SECTION_IDS.workers,
  },
];

const HEADER_STEP_THEMES = {
  violet: {
    dot: "bg-violet-600 text-white shadow-violet-200/80",
    label: "text-violet-800",
    line: "from-violet-300 to-emerald-300",
  },
  emerald: {
    dot: "bg-emerald-600 text-white shadow-emerald-200/80",
    label: "text-emerald-800",
    line: "from-emerald-300 to-sky-300",
  },
  sky: {
    dot: "bg-sky-600 text-white shadow-sky-200/80",
    label: "text-sky-800",
    line: "",
  },
};

const AdvanceModeHeaderStepper = () => {
  const { participantLabelPlural } = useCareLabels();

  return (
  <nav
    className='advance-mode-header-stepper'
    aria-label={`Advance mode workflow: Schedule, ${participantLabelPlural}, Workers`}
  >
    {ADVANCE_HEADER_STEPS.map(({ step, label, labelKey, theme, sectionId }, index) => {
      const styles = HEADER_STEP_THEMES[theme] || HEADER_STEP_THEMES.violet;
      const isLast = index === ADVANCE_HEADER_STEPS.length - 1;
      const stepLabel =
        labelKey === "participants" ? participantLabelPlural : label;

      return (
        <React.Fragment key={step}>
          <button
            type='button'
            className='advance-mode-header-step advance-mode-header-step--clickable'
            onClick={() => scrollToAdvanceSection(sectionId)}
            title={`Go to ${stepLabel}`}
            aria-label={`Scroll to ${stepLabel} section`}
          >
            <span
              className={`advance-mode-header-step__dot ${styles.dot}`}
              aria-hidden
            >
              {step}
            </span>
            <span className={`advance-mode-header-step__label ${styles.label}`}>
              {stepLabel}
            </span>
          </button>
          {!isLast && (
            <span
              className={`advance-mode-header-step__line bg-gradient-to-r ${styles.line}`}
              aria-hidden
            />
          )}
        </React.Fragment>
      );
    })}
  </nav>
  );
};

/** Unified advance mode header — title, workflow stepper, and exit action. */
export const AdvanceModeHeader = ({ onExitAdvanceMode, canExit = true }) => {
  const { participantLabelPlural } = useCareLabels();

  return (
  <header className='advance-mode-header overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] ring-1 ring-slate-900/[0.03]'>
    <div className='advance-mode-header__accent' aria-hidden />

    <div className='relative px-4 py-4 sm:px-5 sm:py-4'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6'>
        <div className='flex min-w-0 flex-1 gap-3 sm:gap-3.5'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25 ring-1 ring-white/20'>
            <HiAdjustments className='h-5 w-5' aria-hidden />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1'>
              <h2 className='text-[15px] font-semibold tracking-tight text-slate-900 sm:text-base'>
                Advanced shift editor
              </h2>
              <span className='inline-flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-indigo-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700'>
                <span className='relative flex h-1.5 w-1.5'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-60' />
                  <span className='relative inline-flex h-1.5 w-1.5 rounded-full bg-indigo-500' />
                </span>
                Active
              </span>
            </div>
            <p className='mt-1.5 max-w-xl text-xs leading-relaxed text-slate-500'>
              Configure {participantLabelPlural.toLowerCase()}, workers, and billing on one shift. Each person keeps
              their own times and line items.
            </p>
          </div>
        </div>

        {canExit && (
          <button
            type='button'
            onClick={onExitAdvanceMode}
            className='group inline-flex w-full shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-slate-200/90 bg-slate-50/80 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/35 lg:w-auto'
          >
            <svg
              className='h-3.5 w-3.5 text-slate-400 transition group-hover:-translate-x-0.5 group-hover:text-indigo-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap='round' strokeLinejoin='round' d='M10 19l-7-7m0 0l7-7m-7 7h18' />
            </svg>
            Back to basic edit
          </button>
        )}
      </div>

      <div className='mt-4 border-t border-slate-100/90 pt-4'>
        <AdvanceModeHeaderStepper />
      </div>
    </div>
  </header>
  );
};

/** @deprecated Use AdvanceModeHeader */
export const AdvanceModeBanner = AdvanceModeHeader;

/** @deprecated Merged into AdvanceModeHeader */
export const AdvanceModeFlowHint = () => null;

export const AdvanceModeShiftOverlapAlert = ({
  shiftOverlap,
  allowShiftOverlapped = false,
  className = "",
}) => {
  if (
    !shiftOverlap?.overlapping_shift?.[0]?.overlapped ||
    allowShiftOverlapped
  ) {
    return null;
  }

  const workerNames = shiftOverlap.overlapping_shift[0]?.worker_name || [];
  const nameText =
    workerNames.length > 0
      ? workerNames.map((item) => `${item}, `).join(" ")
      : "";
  const verb =
    workerNames.length === 1 ? "already has " : "already have ";

  return (
    <div
      className={`mx-3 mb-0 mt-0 flex items-start gap-2 rounded-lg border border-rose-200/80 border-l-4 border-l-rose-500 bg-rose-50/90 p-2.5 text-rose-800 sm:mx-4 ${className}`}
    >
      <FaExclamationCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500' />
      <p className='text-xs font-semibold leading-snug'>
        {nameText}
        {verb}
        shift during this time, causing an overlap. To proceed with scheduling,
        please turn on the &quot;Allow Shift Overlapping&quot; option
      </p>
    </div>
  );
};

const SkeletonBar = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-slate-200/80 ${className}`} />
);

const AdvanceModeSectionSkeleton = ({
  bodyClassName = "",
  children,
  headerExtra,
}) => (
  <section className={advanceSectionClass}>
    <div className='flex items-center gap-2.5 border-b border-slate-100/90 bg-gradient-to-r from-slate-50/90 via-white to-white px-3 py-3.5 sm:px-4'>
      <SkeletonBar className='h-9 w-9 shrink-0 rounded-xl' />
      <div className='min-w-0 flex-1 space-y-2'>
        <SkeletonBar className='h-3.5 w-28' />
        <SkeletonBar className='h-2.5 w-44 max-w-full' />
      </div>
      {headerExtra}
    </div>
    <div className={`px-3 py-3 sm:px-4 sm:py-3.5 ${bodyClassName}`}>{children}</div>
  </section>
);

/** Layout skeleton shown while switching into / lazy-loading advance mode. */
export const AdvanceModeViewSkeleton = () => (
  <div
    className='advance-mode-root min-w-0 max-w-full space-y-3 p-0.5 pt-1 sm:space-y-4 sm:p-1'
    aria-busy='true'
    aria-label='Loading advanced edit'
  >
    <header className='advance-mode-header overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] ring-1 ring-slate-900/[0.03]'>
      <div className='advance-mode-header__accent' aria-hidden />
      <div className='relative space-y-4 px-4 py-4 sm:px-5'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
          <div className='flex min-w-0 flex-1 gap-3'>
            <SkeletonBar className='h-11 w-11 shrink-0 rounded-xl' />
            <div className='min-w-0 flex-1 space-y-2 pt-0.5'>
              <SkeletonBar className='h-4 w-36' />
              <SkeletonBar className='h-2.5 w-full max-w-md' />
            </div>
          </div>
          <SkeletonBar className='h-9 w-full rounded-xl lg:w-36' />
        </div>
        <div className='flex items-center justify-center gap-2 border-t border-slate-100/90 pt-4'>
          <SkeletonBar className='h-7 w-7 rounded-full' />
          <SkeletonBar className='h-2 w-10' />
          <SkeletonBar className='h-7 w-7 rounded-full' />
          <SkeletonBar className='h-2 w-10' />
          <SkeletonBar className='h-7 w-7 rounded-full' />
        </div>
      </div>
    </header>

    <AdvanceModeSectionSkeleton>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <div className='space-y-2'>
          <SkeletonBar className='h-2.5 w-16' />
          <SkeletonBar className='h-10 w-full rounded-lg' />
        </div>
        <div className='space-y-2'>
          <SkeletonBar className='h-2.5 w-20' />
          <SkeletonBar className='h-10 w-full rounded-lg' />
        </div>
        <div className='space-y-2'>
          <SkeletonBar className='h-2.5 w-16' />
          <SkeletonBar className='h-10 w-full rounded-lg' />
        </div>
      </div>
    </AdvanceModeSectionSkeleton>

    <AdvanceModeSectionSkeleton>
      <div className='flex items-center gap-3'>
        <SkeletonBar className='h-5 w-10 rounded-full' />
        <SkeletonBar className='h-3 w-40' />
      </div>
    </AdvanceModeSectionSkeleton>

    <div className='advance-mode-participant-worker-grid grid min-w-0 grid-cols-1 items-stretch gap-3 sm:gap-4 xl:grid-cols-2'>
      {[0, 1].map((col) => (
        <AdvanceModeSectionSkeleton
          key={col}
          bodyClassName='space-y-3'
          headerExtra={<SkeletonBar className='h-6 w-16 rounded-full' />}
        >
          <SkeletonBar className='h-2.5 w-20' />
          <SkeletonBar className='h-10 w-full rounded-lg' />
          <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/40 p-3'>
            <div className='flex items-center gap-2'>
              <SkeletonBar className='h-8 w-8 rounded-full' />
              <SkeletonBar className='h-3 w-28' />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <SkeletonBar className='h-9 w-full rounded-lg' />
              <SkeletonBar className='h-9 w-full rounded-lg' />
            </div>
          </div>
          <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/40 p-3'>
            <div className='flex items-center gap-2'>
              <SkeletonBar className='h-8 w-8 rounded-full' />
              <SkeletonBar className='h-3 w-24' />
            </div>
            <SkeletonBar className='h-9 w-full rounded-lg' />
          </div>
        </AdvanceModeSectionSkeleton>
      ))}
    </div>

    <AdvanceModeSectionSkeleton>
      <div className='space-y-3'>
        <SkeletonBar className='h-9 w-full rounded-lg' />
        <SkeletonBar className='h-9 w-full rounded-lg' />
        <SkeletonBar className='h-20 w-full rounded-lg' />
      </div>
    </AdvanceModeSectionSkeleton>
  </div>
);

/** Full-screen edit layout skeleton while V5 advance shift details are loading. */
export const AdvanceModeUpdateSkeleton = () => (
  <div
    className='flex min-w-0 flex-col gap-3 p-2 xl:flex-row'
    aria-busy='true'
    aria-label='Loading advance shift details'
  >
    <div className='min-w-0 w-full flex-1 space-y-2'>
      <div className='rounded-xl bg-slate-100/50 p-1 sm:p-2'>
        <AdvanceModeViewSkeleton />
      </div>
    </div>
    <div className='w-full shrink-0 space-y-3 xl:w-56'>
      <div className='advance-mode-root'>
        <AdvanceModeSectionSkeleton bodyClassName='space-y-0 pt-0'>
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <div
              key={row}
              className='flex items-center justify-between border-b border-slate-100 py-2.5 last:border-b-0'
            >
              <SkeletonBar className='h-3 w-24' />
              <SkeletonBar className='h-5 w-9 rounded-full' />
            </div>
          ))}
        </AdvanceModeSectionSkeleton>
      </div>
      <div className='advance-mode-root'>
        <AdvanceModeSectionSkeleton bodyClassName='space-y-3'>
          <SkeletonBar className='h-2.5 w-28' />
          <SkeletonBar className='h-9 w-full rounded-lg' />
          <SkeletonBar className='h-9 w-full rounded-lg' />
        </AdvanceModeSectionSkeleton>
      </div>
    </div>
  </div>
);

export const AdvanceModeWorkerAvailabilityAlert = ({
  shiftOverlap,
  onCheckAvailability,
  getWorkerProfileLoader = false,
  loadingItemId = null,
  className = "",
}) => {
  const availability = shiftOverlap?.availability;
  if (!availability?.length) return null;

  return (
    <div className={`mx-3 space-y-2 sm:mx-4 ${className}`}>
      {availability.map((item) => (
        <div
          key={item?.uuid ?? item?.name}
          className='flex items-start gap-2 rounded-lg border border-amber-200/80 border-l-4 border-l-amber-500 bg-amber-50/90 p-2.5 text-amber-900'
        >
          <IoWarningOutline className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500' />
          <p className='text-xs font-semibold leading-snug'>
            {item?.name} is not available for the selected time slot,
            <span
              role='button'
              tabIndex={0}
              onClick={() => onCheckAvailability?.(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onCheckAvailability?.(item);
                }
              }}
              className='ml-1 cursor-pointer text-sm text-indigo-600 underline hover:text-indigo-800'
            >
              {getWorkerProfileLoader && loadingItemId === item?.uuid ? (
                <Loader cssClass='ml-2' isScheduleScreen isSmallView />
              ) : (
                " Please click here to check their availability."
              )}
            </span>
          </p>
        </div>
      ))}
    </div>
  );
};
